import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { Store } from '@ngxs/store';
import {
  AuthState,
  ConfigService,
  LanguageState,
  OrgConfiguration,
  UserPreferencesActions,
  UserPreferencesState,
} from '@osos/shared/legacy-core';
import {
  GeneralModelPresenterModel,
  LANGUAGE_ID,
  LanguagePresenterModel,
  THEME_ID,
  ThemePresenterModel,
  TIME_ZONE_ID,
  TimeZonePresenterModel,
  UserStateDataPresenterModel,
} from '@osos/shared/types';

import { HttpClient } from '@angular/common/http';
import { ActionItem, ActionItemType } from '@osos/shared/utility';
import { combineLatest, Observable } from 'rxjs';
import { map, shareReplay, takeUntil, tap } from 'rxjs/operators';
import { ThemeService } from '../../services/theme.service';
import { BaseUiThemeDirective } from '../base/base.directive';
import { MainComponent } from '../main/main.component';
const VERSION_URL = 'version.json';

@Component({
  selector: 'app-config',
  templateUrl: './config.component.html',
  styleUrls: ['./config.component.scss'],
  standalone: false,
})
export class ConfigComponent extends BaseUiThemeDirective implements OnInit {
  webVersion$ = this.http.get<any>(VERSION_URL).pipe(
    map((data) => data?.WEB_VERSION),
    shareReplay({ bufferSize: 1, refCount: false }),
  );
  backendVersion$: Observable<string>;
  configActive = false;
  isInputBackgroundChanged = false;
  themes$: Observable<ThemePresenterModel[]>;
  languages$: Observable<LanguagePresenterModel[]>;
  timeZones$: Observable<TimeZonePresenterModel[]>;
  dates$: Observable<GeneralModelPresenterModel[]>;
  times$: Observable<GeneralModelPresenterModel[]>;
  dateTimes$: Observable<GeneralModelPresenterModel[]>;
  configurationAction: ActionItem;
  configurationActions: ActionItem[] = [];
  themeName: ThemePresenterModel;
  userState$: UserStateDataPresenterModel;
  language: LanguagePresenterModel;
  timeZone: TimeZonePresenterModel;
  dateFormat: GeneralModelPresenterModel;
  timeFormat: GeneralModelPresenterModel;
  dateTime: GeneralModelPresenterModel;
  format: any;
  rtlDir$: Observable<boolean> = this.store.select(
    LanguageState.currentLanguageRTLDirection,
  );
  _themes: ThemePresenterModel[];
  _languages: LanguagePresenterModel[];
  _timeZones: TimeZonePresenterModel[];
  _dates: GeneralModelPresenterModel[];
  _times: GeneralModelPresenterModel[];
  _dateTimes: GeneralModelPresenterModel[];
  isAnonymous = true;
  get orgConfig(): OrgConfiguration {
    return this.config.orgConfig;
  }

  get currentYear() {
    return new Date().getFullYear();
  }

  constructor(
    public appMain: MainComponent,
    public store: Store,
    public themeSer: ThemeService,
    private config: ConfigService,
    private cdr: ChangeDetectorRef,
    private http: HttpClient,
  ) {
    super();
  }

  trackByFn(index: number): number {
    return index;
  }
  ngOnInit(): void {
    this.isAnonymous = !this.store.selectSnapshot(AuthState.isAuthenticated);
    this.store
      .select(UserPreferencesState.userPreferences)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        this.menuMode = res?.menuMode;
        this.layoutMode = res?.layoutMode;
        this.fontScale = res?.fontSize;
      });

    this.backendVersion$ = this.store.select(
      UserPreferencesState.backendVersion,
    );

    this.initConfigurationAction();
    this.getTheme();

    this.getLanguage();
    this.getTimeZone();
    this.getDateFormat();
    this.getTimeFormat();
    this.getDateTimes();

    // this.getUserSate();
    combineLatest([
      this.store.select(UserPreferencesState.selectUserState),
      this.themes$,
      this.languages$,
      this.timeZones$,
      this.dates$,
      this.times$,
      this.dateTimes$,
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe((resp) => {
        this.userState$ = resp[0];
        this.stateProcess();
      });
  }
  initConfigurationAction() {
    this.configurationActions = [];
    this.configurationAction = null;
    this.configurationAction = {
      id: `${ActionItemType.MenuItem}-save-per-session`,
      type: ActionItemType.MenuItem,
      icon: 'pi pi-save',
      label: 'Save Per Session',
      localeLabel: 'base.actions.savePerSession',
      command: () => {
        this.saveConfigurationInSessionStorage();
      },
    };
    this.configurationActions.push(this.configurationAction);
  }
  getTheme() {
    this.themes$ = this.store.select(UserPreferencesState.selectTheme).pipe(
      tap((res) => {
        this._themes = res;
      }),
    );
  }
  getLanguage() {
    // this.store.dispatch(new DisplayAvailableLanguages());
    this.languages$ = this.store.select(LanguageState.selectLanguages).pipe(
      tap((res) => {
        this._languages = res;
      }),
    );
  }
  getTimeZone() {
    this.timeZones$ = this.store
      .select(UserPreferencesState.selectTimeZone)
      .pipe(
        tap((res) => {
          this._timeZones = res;
        }),
      );
  }
  getDateFormat() {
    this.dates$ = this.store.select(UserPreferencesState.selectDate).pipe(
      tap((res) => {
        this._dates = res;
      }),
    );
  }
  getTimeFormat() {
    this.times$ = this.store.select(UserPreferencesState.selectTime).pipe(
      tap((res) => {
        this._times = res;
      }),
    );
  }
  getDateTimes() {
    this.dateTimes$ = this.store
      .select(UserPreferencesState.selectDateTime)
      .pipe(
        tap((res) => {
          this._dateTimes = res;
        }),
      );
  }

  stateProcess() {
    this.language = this._languages?.find(
      (x) => x.id === this.userState$.language?.dataEntry,
    );
    this.themeName = this._themes?.find(
      (x) => x.id === this.userState$.theme?.dataEntry,
    );
    if (this.themeName && !this.layoutMode) {
      this.layoutMode = this.themeName?.dark_mode ? 'dark' : 'light';
      this.onLayoutModeChange(null, this.layoutMode);
    }
    this.timeZone = this._timeZones?.find(
      (x) => x.id === this.userState$.timeZone?.dataEntry,
    );
    this.dateFormat = this._dates?.find(
      (x) => x.id === this.userState$.dateFormat?.id,
    );
    this.dateTime = this._dateTimes?.find(
      (x) => x.id === this.userState$.dateTimeFormat?.id,
    );
    this.timeFormat = this._times?.find(
      (x) => x.id === this.userState$.timeFormat?.id,
    );
    this.cdr.detectChanges();
  }

  saveConfigurationInSessionStorage() {
    const configuration: UserStateDataPresenterModel = {
      language: {
        dataEntity: LANGUAGE_ID,
        dataEntry: this.language?.id,
        _resolved: {
          mainTitle: this.language?.code,
        },
      },
      theme: {
        dataEntity: THEME_ID,
        dataEntry: this.themeName?.id,
      },
      timeZone: {
        dataEntity: TIME_ZONE_ID,
        dataEntry: this.timeZone?.id,
        _resolved: {
          mainTitle: { en: this.timeZone?.time_zone_id },
        },
      },
      dateFormat: {
        id: this.dateFormat?.id,
        _resolved: {
          mainTitle: this.dateFormat?.value,
        },
      },
      timeFormat: {
        id: this.timeFormat?.id,
        _resolved: {
          mainTitle: this.timeFormat?.value,
        },
      },
      dateTimeFormat: {
        id: this.dateTime?.id,
        _resolved: {
          mainTitle: this.dateTime?.value,
        },
      },
      menuMode: this.menuMode,
      layoutMode: this.layoutMode,
      fontSize: this.fontScale,
    };
    sessionStorage.setItem('configuration', JSON.stringify(configuration));
    window.location.reload();
  }
  savePerAccount() {
    this.store
      .dispatch(
        new UserPreferencesActions.UpdateUserStateData(this.getSettingValue()),
      )
      .subscribe({
        next: () => {
          this.configActive = false;
        },
      });
  }
  cancel() {
    this.configActive = false;
    const currentState = this.store.selectSnapshot(
      UserPreferencesState.selectUserState,
    );
    if (this.fontScale !== currentState?.fontSize) {
      this.fontScale = currentState?.fontSize;
      this.applyScale();
    }
    if (this.layoutMode !== currentState?.layoutMode) {
      this.layoutMode = currentState?.layoutMode;
      this.onLayoutModeChange(null, this.layoutMode);
    }
    if (this.menuMode !== currentState?.menuMode) {
      this.menuMode = currentState?.menuMode;
      this.onChangeMenuMode(this.menuMode);
    }
    this.userState$ = currentState;
    this.stateProcess();
  }
  open() {
    this.configActive = true;
  }
  decrementScale(): void {
    this.fontScale--;
    this.applyScale();
  }

  incrementScale(): void {
    this.fontScale++;
    this.applyScale();
  }

  applyScale(): void {
    this.store.dispatch(new UserPreferencesActions.SetFontSize(this.fontScale));
    this.themeSer.changeFontSize(this.fontScale);
  }

  onChangeMenuMode(event): void {
    this.store.dispatch(new UserPreferencesActions.SetMenuMode(event));
  }

  onInputStyleClick(): void {
    this.isInputBackgroundChanged = true;
  }

  onLayoutModeChange(event, mode): void {
    this.store.dispatch(
      new UserPreferencesActions.SetUserPreferences({
        layoutMode: mode,
      }),
    );
    this.themeSer.changeLayoutMode(mode);
  }

  isIE(): boolean {
    return /(MSIE|Trident\/|Edge\/)/i.test(window.navigator.userAgent);
  }
  resetToDefault() {
    sessionStorage.removeItem('configuration');
    this.store.dispatch(new UserPreferencesActions.ResetUserStateToDefault());
  }

  private getSettingValue() {
    return {
      dateFormat: this.dateFormat?.id
        ? {
            id: this.dateFormat?.id,
          }
        : null,
      dateTimeFormat: this.dateTime?.id
        ? {
            id: this.dateTime?.id,
          }
        : null,
      theme: this.themeName?.id
        ? {
            dataEntity: THEME_ID,
            dataEntry: this.themeName?.id,
          }
        : null,
      timeFormat: this.timeFormat?.id
        ? {
            id: this.timeFormat?.id,
          }
        : null,
      timeZone: this.timeZone?.id
        ? {
            dataEntity: TIME_ZONE_ID,
            dataEntry: this.timeZone?.id,
          }
        : null,
      fontSize: this.fontScale,
      menuMode: this.menuMode,
      layoutMode: this.layoutMode,
      language: this.language?.id
        ? {
            dataEntity: LANGUAGE_ID,
            dataEntry: this.language?.id,
          }
        : null,
    };
  }

  toggleConfigMenu() {
    this.configActive = !this.configActive;
    if (this.configActive) {
      this.store.dispatch(
        new UserPreferencesActions.DisplayAvailableDateTimes(),
      );
      this.store.dispatch(
        new UserPreferencesActions.DisplayAvailableTimeZones(),
      );
      this.store.dispatch(new UserPreferencesActions.DisplayAvailableTimes());
      this.store.dispatch(new UserPreferencesActions.DisplayAvailableDates());
      this.store.dispatch(new UserPreferencesActions.GetBackendVersion());
    }
  }
}
